/**
 * App Sidebar – navigation using shadcn Sidebar (replaces TopNavbar)
 * @see https://ui.shadcn.com/docs/components/radix/sidebar
 */

import * as React from "react";
import { Link, useLocation } from "wouter";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Users,
  Shield,
  Wallet,
  UserCheck,
  List,
  CreditCard,
  History,
  Send,
  UserX,
  Mail,
  Briefcase,
  ChevronRight,
  ChevronUp,
  LogOut,
  Sun,
  Moon,
  FileText,
  User,
  Key,
  BookOpen,
  Loader2,
  KeyRound,
} from "lucide-react";
import { Avatar } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  canAccessKeuanganRekening,
  canAccessKeuanganTransferPlans,
  canAccessSilentKeyAdmin,
  hasAnyPrivilege,
  hasAssignmentPrivilege,
  hasEffectiveNasionalAdminAssignment,
  hasSuperAdminUserLevel,
} from "@/utils/privileges";
import type { AdminPrivilege } from "@/services/adminService";
import { getUserInitials } from "./utils/userDetailUtils";

interface AppSidebarProps {
  onLogoutClick?: () => void;
  isLoggingOut?: boolean;
  logoutError?: string | null;
}

export function AppSidebar({ onLogoutClick, isLoggingOut = false, logoutError }: AppSidebarProps) {
  const [location] = useLocation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const initials = user ? getUserInitials(user) : "?";

  const canAccess = (privileges: AdminPrivilege[]) =>
    privileges.length === 0 || hasAnyPrivilege(user, privileges);

  const showSilentKeyMenu = canAccessSilentKeyAdmin(user);
  const showKeanggotaanDaftar = hasAssignmentPrivilege(user, "manage_users");
  const showKeanggotaanPenangguhan = hasAssignmentPrivilege(user, "manage_blocks");
  const showFotoKtpMenu = hasSuperAdminUserLevel(user);
  const showNriRequestsMenu = hasAssignmentPrivilege(user, "manage_users");
  const showKeanggotaanGroup =
    showKeanggotaanDaftar || showKeanggotaanPenangguhan || showSilentKeyMenu || showFotoKtpMenu || showNriRequestsMenu;

  const showKeuanganRekening = canAccessKeuanganRekening(user);
  const showKeuanganBuatRencana = hasEffectiveNasionalAdminAssignment(user) && canAccess(["create_transaction_plan", "manage_payments"]);
  const showKeuanganRiwayatTransfer = hasEffectiveNasionalAdminAssignment(user) && canAccess([
    "manage_historical_transfer",
    "manage_payments",
  ]);
  const showKeuanganDaftarRencana = canAccessKeuanganTransferPlans(user);
  const showKeuanganRiwayatTransaksi = canAccess([
    "manage_transactions",
    "manage_transactions_plan",
    "view_reports",
  ]);
  const showKeuanganMenu =
    showKeuanganRekening ||
    showKeuanganBuatRencana ||
    showKeuanganDaftarRencana ||
    showKeuanganRiwayatTransaksi ||
    showKeuanganRiwayatTransfer;

  const isActive = (path: string) => location === path;
  const isActiveGroup = (paths: string[]) => paths.some((p) => location.startsWith(p) || location === p);

  return (
    <Sidebar collapsible="icon" className="border-r border-red-500/30 bg-gradient-to-b from-red-600 via-red-700 to-red-800 text-white">
      <SidebarHeader className="border-b border-white/10 p-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/15 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:w-8 backdrop-blur-sm">
            <img
              src="https://orari.or.id/wp-content/uploads/2021/12/orari-png24-133x300-1.png"
              alt="ORARI"
              className="h-7 w-auto object-contain brightness-0 invert group-data-[collapsible=icon]:h-5"
            />
          </div>
          <div className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="text-base font-bold tracking-wide">ORARI</span>
            <span className="text-xs text-white/60 leading-tight">Organisasi Amatir Radio Indonesia</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-white/50 text-sm uppercase tracking-wider">Menu</SidebarGroupLabel>
          <SidebarMenu>
            {showKeanggotaanGroup && (
              <Collapsible
                asChild
                defaultOpen={isActiveGroup(["/keanggotaan"])}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Keanggotaan"
                      isActive={isActiveGroup(["/keanggotaan"])}
                      className="text-white/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white data-[active=true]:font-medium text-base"
                    >
                      <UserCheck className="size-4" />
                      <span>Keanggotaan</span>
                      <ChevronRight className="ml-auto size-4 text-white/50 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="bg-black/15 border-white/10">
                      {showKeanggotaanDaftar && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keanggotaan/daftar")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keanggotaan/daftar">
                              <List className="size-4" />
                              <span>Daftar Anggota</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showFotoKtpMenu && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keanggotaan/foto-ktp")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keanggotaan/foto-ktp">
                              <CreditCard className="size-4" />
                              <span>Foto KTP</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showKeanggotaanPenangguhan && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keanggotaan/penangguhan")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keanggotaan/penangguhan">
                              <UserX className="size-4" />
                              <span>Penangguhan</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showSilentKeyMenu && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keanggotaan/silent-key")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keanggotaan/silent-key">
                              <KeyRound className="size-4" />
                              <span>Silent Key</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showNriRequestsMenu && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keanggotaan/nri-requests")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keanggotaan/nri-requests">
                              <FileText className="size-4" />
                              <span>Permintaan NRI</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}

            {canAccess(["manage_users"]) && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.startsWith("/logbook")}
                  tooltip="Logbook"
                  className="text-white/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white data-[active=true]:font-medium text-base"
                >
                  <Link href="/logbook">
                    <BookOpen className="size-4" />
                    <span>Logbook</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canAccess(["manage_users"]) && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.startsWith("/qsl")}
                  tooltip="QSL"
                  className="text-white/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white data-[active=true]:font-medium text-base"
                >
                  <Link href="/qsl/sync">
                    <Mail className="size-4" />
                    <span>QSL</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )}

            {canAccess(["manage_jabatan"]) && hasEffectiveNasionalAdminAssignment(user) && (
              <Collapsible
                asChild
                defaultOpen={isActiveGroup(["/management"])}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Kepengurusan"
                      isActive={isActive("/management")}
                      className="text-white/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white data-[active=true]:font-medium text-base"
                    >
                      <Users className="size-4" />
                      <span>Kepengurusan</span>
                      <ChevronRight className="ml-auto size-4 text-white/50 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="bg-black/15 border-white/10">
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={isActive("/management")}
                          className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                        >
                          <Link href="/management">
                            <Briefcase className="size-4" />
                            <span>Jabatan & Penugasan</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}

            {canAccess(["manage_admin_assignments"]) && (
              <Collapsible
                asChild
                defaultOpen={isActiveGroup(["/admin-management"])}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Administrator"
                      isActive={isActive("/admin-management")}
                      className="text-white/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white data-[active=true]:font-medium text-base"
                    >
                      <Shield className="size-4" />
                      <span>Administrator</span>
                      <ChevronRight className="ml-auto size-4 text-white/50 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="bg-black/15 border-white/10">
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          isActive={isActive("/admin-management")}
                          className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                        >
                          <Link href="/admin-management">
                            <Shield className="size-4" />
                            <span>Manajemen Admin</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}

            {showKeuanganMenu && (
              <Collapsible
                asChild
                defaultOpen={isActiveGroup(["/keuangan", "/historical-transaction"])}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip="Keuangan"
                      isActive={isActiveGroup(["/keuangan", "/historical-transaction"])}
                      className="text-white/90 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/20 data-[active=true]:text-white data-[active=true]:font-medium text-base"
                    >
                      <Wallet className="size-4" />
                      <span>Keuangan</span>
                      <ChevronRight className="ml-auto size-4 text-white/50 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="bg-black/15 border-white/10">
                      {showKeuanganRekening && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keuangan/rekening")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keuangan/rekening">
                              <CreditCard className="size-4" />
                              <span>Rekening</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showKeuanganBuatRencana && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keuangan/transfer-plan")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keuangan/transfer-plan">
                              <FileText className="size-4" />
                              <span>Buat Rencana Transfer</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showKeuanganDaftarRencana && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/keuangan/transfer-plans")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keuangan/transfer-plans">
                              <List className="size-4" />
                              <span>Daftar Rencana Transfer</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showKeuanganRiwayatTransaksi && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            isActive={isActive("/historical-transaction")}
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/historical-transaction">
                              <History className="size-4" />
                              <span>Riwayat Transaksi</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                      {showKeuanganRiwayatTransfer && (
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            asChild
                            className="text-white/80 hover:bg-white/10 hover:text-white data-[active=true]:bg-white/15 data-[active=true]:text-white data-[active=true]:font-medium text-sm"
                          >
                            <Link href="/keuangan/riwayat-transfer">
                              <Send className="size-4" />
                              <span>Riwayat Transfer</span>
                            </Link>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      )}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-white/10 p-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="text-white hover:bg-white/10 data-[state=open]:bg-white/10 cursor-pointer"
            >
              <Avatar
                src={user?.foto}
                alt={user?.name || "User"}
                fallback={initials}
                className="h-8 w-8 shrink-0 ring-2 ring-white/20"
              />
              <div className="flex flex-col text-left min-w-0 group-data-[collapsible=icon]:hidden">
                <span className="text-sm font-medium truncate">{user?.name || "User"}</span>
                <span className="text-xs text-white/50 truncate">{user?.callsign || ""}</span>
              </div>
              <ChevronUp className="ml-auto size-4 text-white/50 shrink-0 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56 mb-1">
            <DropdownMenuItem asChild>
              <Link href="/user" className="flex cursor-pointer items-center gap-2">
                <User className="size-4" />
                Profile
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link href="/user#ubah-password" className="flex cursor-pointer items-center gap-2">
                <Key className="size-4" />
                Ubah Password
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleTheme} className="flex cursor-pointer items-center gap-2">
              {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
              Mode {theme === "dark" ? "Terang" : "Gelap"}
            </DropdownMenuItem>
            {logoutError && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-xs text-destructive">{logoutError}</div>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogoutClick}
              disabled={isLoggingOut}
              className="text-destructive focus:text-destructive flex cursor-pointer items-center gap-2"
            >
              {isLoggingOut ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <LogOut className="size-4" />
              )}
              Keluar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>

      <SidebarRail className="hover:bg-white/10" />
    </Sidebar>
  );
}
